import {
  CommunityInfo, GeneralSocialInfo, ProjectInvestorInfo,
  ProjectStakeHolderInfo, ProjectTeamInfo,
  TechAditInfo,
  TokenDescriptionInfo,
  TokenDetailInfo,
  TokenIssuanceInfo
} from '~/api/repository'
import { SocialModel } from '~/types/model/general/social.model'
import { InvestorModel, StakeholderModel, TeamModel } from '~/types/model/stakeholder.model'
import { isEmptyList } from '~/utils/array.util'
import { formatAbbrDate } from '~/utils/date'
import { setComma } from '~/utils/number'

class TechAuditModel {
  private readonly _type: string
  private readonly _name?: string
  private readonly _url?: string

  constructor (res: TechAditInfo) {
    this._type = res.TP
    this._name = res.NM
    this._url = res.URL
  }

  get type () {
    return this._type
  }

  get name () {
    return this._name
  }

  get url () {
    return this._url
  }
}

class TokenAboutModel {
  private readonly _name: string
  private readonly _categoryList: Array<string>
  private readonly _symbol: string
  private readonly _communityLink: Array<CommunityInfo>
  private readonly _description: string
  private readonly _officialLink: string
  private readonly _summary: string
  private readonly _otherLink: Array<GeneralSocialInfo>

  constructor (res: TokenDescriptionInfo) {
    this._name = res.NM
    this._categoryList = res.CATG
    this._symbol = res.SB
    this._communityLink = res.COMM
    this._description = res.DESC
    this._officialLink = res.OFFLK
    this._summary = res.SM
    this._otherLink = res.OTHLK
  }

  get name () {
    return this._name
  }

  get categoryList () {
    return this._categoryList
  }

  get symbol () {
    return this._symbol
  }

  get communityLink () {
    if (isEmptyList(this._communityLink)) {
      return []
    }
    return this._communityLink.map(community => {
      return new SocialModel(community.CH, community.CMTP, community.URL ?? '')
    })
  }

  get description () {
    return this._description
  }

  get officialLink () {
    return this._officialLink
  }

  get summary () {
    return this._summary
  }

  get otherLink () {
    if (isEmptyList(this._otherLink)) {
      return []
    }
    return this._otherLink.map(other => {
      return new SocialModel(other.CH, other.SOCTP, other.LNK)
    })
  }
}

class IcoModel {
  private readonly _publishStartDate: string
  private readonly _deadLine: string
  private readonly _issueMainnet: string
  private readonly _totalRaised: string
  private readonly _totalSupply: string
  private readonly _icoConversion: string
  private readonly _techAudit: Array<TechAditInfo>
  private readonly _whitePaperUrl: string
  private readonly _whitePaperSummary: string
  private readonly _contributors: string

  constructor (res: TokenIssuanceInfo) {
    this._publishStartDate = res.PSDT
    this._deadLine = res.DL
    this._issueMainnet = res.IMN
    this._totalRaised = res.TR
    this._totalSupply = res.TSUP
    this._icoConversion = res.IC
    this._techAudit = res.TECHADT
    this._whitePaperUrl = res.WPURL
    this._whitePaperSummary = res.WPSM
    this._contributors = res.CTBRS
  }

  get publishStartDate () {
    return formatAbbrDate(this._publishStartDate)
  }

  get deadLine () {
    return formatAbbrDate(this._deadLine)
  }

  get issueMainnet () {
    return this._issueMainnet || '-'
  }

  get totalRaised () {
    return setComma(this._totalRaised)
  }

  get totalSupply () {
    return setComma(this._totalSupply)
  }

  get icoConversion () {
    return setComma(this._icoConversion)
  }

  get techAudit () {
    return this._techAudit.map(tech => new TechAuditModel(tech))
  }

  get whitePaperUrl () {
    return this._whitePaperUrl
  }

  get whitePaperSummary () {
    return this._whitePaperSummary
  }

  get contributors () {
    return setComma(this._contributors)
  }
}

export class TokenDetailModel {
  private readonly _tokenId: string
  private readonly _about: TokenDescriptionInfo
  private readonly _ico: TokenIssuanceInfo
  private readonly _stakeholders: ProjectStakeHolderInfo
  private readonly _team: ProjectTeamInfo
  private readonly _investor: ProjectInvestorInfo

  constructor (res: TokenDetailInfo) {
    this._tokenId = res.TKNID
    this._about = res.ABT
    this._ico = res.ICO
    this._stakeholders = res.STHDR
    this._team = res.TM
    this._investor = res.IVTR
  }

  get tokenId () {
    return this._tokenId
  }

  get about () {
    return new TokenAboutModel(this._about)
  }

  get ico () {
    return new IcoModel(this._ico)
  }

  get stakeholders () {
    return new StakeholderModel(this._stakeholders)
  }

  get team () {
    return new TeamModel(this._team)
  }

  get investor () {
    return new InvestorModel(this._investor)
  }

  get infoList () {
    return [
      {
        icon: 'generating_tokens',
        label: 'Token Name',
        type: 'text',
        contents: [ {
          label: this.about.name
        } ]
      },
      {
        icon: 'group',
        label: 'Community',
        type: 'link',
        contents: this.about.communityLink.map(community => {
          return {
            label: community.name,
            value: community.link,
          }
        })
      },
      {
        icon: 'web',
        label: 'Official Link',
        type: this.about.officialLink ? 'link' : 'text',
        contents: this.about.officialLink ? [ {
          label: this.about.officialLink,
          value: this.about.officialLink
        } ] : ''
      },
      {
        icon: 'travel_explore',
        label: 'Discover',
        type: 'link',
        contents: this.about.otherLink.map(other => {
          return {
            label: other.name,
            value: other.link
          }
        })
      },
      {
        icon: 'category',
        label: 'Category',
        type: 'chip',
        contents: this.about.categoryList.map(category => {
          return {
            label: category,
            value: category
          }
        })
      }
    ]
  }
}